<script setup lang="ts">
import type { MerchantProfile } from '@/modules/auth/types/Merchants';

const { isMobile = false, topStores = false } = defineProps<{
  isMobile?: boolean;
  topStores?: boolean;
}>();

const { data: merchants, execute: getMerchants, pending } = useGetData<{ data: MerchantProfile[] }>();
pending.value = true;

const getFeaturedPosition = (merchant: MerchantProfile) => merchant.featured_position ?? merchant.featured_order ?? Infinity;

const filteredStores = computed(() => {
  const data = merchants.value?.data;
  if (!data) return data;
  if (!topStores) return data;
  return [...data].sort((a, b) => {
    return getFeaturedPosition(a) - getFeaturedPosition(b);
  });
});

const storeIcons = ['mdi-wrench-outline', 'mdi-clock-outline', 'mdi-truck-fast-outline', 'mdi-shield-check-outline'];
const getInitials = (merchant: MerchantProfile) =>
  merchant.business_name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(word => word[0]?.toUpperCase() || '')
    .join('');

const merchantCategory = 'Trade Supplier';

onMounted(() => {
  getMerchants(`/merchants?include=logo,coverPhoto${topStores ? '&filter[top_stores]=8' : ''}`);
});
</script>

<template>
  <section v-if="pending || filteredStores?.length" class="nearby-stores mt-4 mb-6">
    <div class="nearby-stores-container browse-max-screen-size">
      <h2 class="nearby-stores-title">
        Stores
      </h2>

    <!-- Mobile Skeleton -->
    <v-slide-group v-if="isMobile && pending" class="stores is-mobile" content-class="stores__content">
      <v-slide-group-item v-for="merchant in 6" :key="merchant">
        <div class="store-card-skeleton">
          <v-skeleton-loader type="image, list-item-avatar, text, text"></v-skeleton-loader>
        </div>
      </v-slide-group-item>
    </v-slide-group>

    <!-- Mobile Display -->
    <v-slide-group
      v-else-if="isMobile && !pending"
      class="stores is-mobile"
      :show-arrows="false"
      content-class="stores__content"
    >
      <v-slide-group-item v-for="(merchant, index) in filteredStores" :key="merchant.id">
        <NuxtLink :to="`/browse/stores/${merchant.id}`" class="store-card mobile-card">
          <div class="store-card__media">
            <v-img
              v-if="merchant.cover_photo?.thumb_url"
              :src="merchant.cover_photo.thumb_url"
              class="store-card__cover"
              cover
            />
            <div v-else class="store-card__placeholder">
              <v-icon class="store-card__icon" size="46">
                {{ storeIcons[index % storeIcons.length] }}
              </v-icon>
              <span class="store-card__placeholder-text">{{ merchant.business_name }}</span>
            </div>
            <div v-if="merchant.cover_photo?.thumb_url" class="store-card__media-overlay" />
            <span v-if="merchant.featured_label" class="store-card__badge">{{ merchant.featured_label }}</span>
          </div>

          <div class="store-card__body">
            <div class="store-card__identity">
              <div class="store-card__logo">
                <v-img
                  v-if="merchant.logo?.thumb_url"
                  class="merchant-logo"
                  :aspect-ratio="1"
                  :src="merchant.logo.thumb_url"
                  width="44"
                  height="44"
                  cover
                />
                <span v-else>{{ getInitials(merchant) }}</span>
              </div>

              <div class="store-card__text">
                <div class="store-card__name">
                  {{ merchant.business_name }}
                </div>
                <div class="store-card__subtitle">
                  {{ merchantCategory }}
                </div>
              </div>
            </div>
          </div>
        </NuxtLink>
      </v-slide-group-item>
    </v-slide-group>

    <!-- Desktop Skeleton -->
    <div v-else-if="!isMobile && pending" class="stores-grid">
      <div v-for="merchant in 6" :key="merchant" class="store-card-skeleton">
        <v-skeleton-loader type="image, list-item-avatar, text, text"></v-skeleton-loader>
      </div>
    </div>

    <!-- Desktop Display -->
    <div v-else-if="!isMobile && !pending" class="stores-grid">
      <NuxtLink v-for="(merchant, index) in filteredStores" :key="merchant.id" :to="`/browse/stores/${merchant.id}`" class="store-card">
        <div class="store-card__media">
          <v-img
            v-if="merchant.cover_photo?.thumb_url"
            :src="merchant.cover_photo.thumb_url"
            class="store-card__cover"
            cover
          />
          <div v-else class="store-card__placeholder">
            <v-icon class="store-card__icon" size="54">
              {{ storeIcons[index % storeIcons.length] }}
            </v-icon>
            <span class="store-card__placeholder-text">{{ merchant.business_name }}</span>
          </div>
          <div v-if="merchant.cover_photo?.thumb_url" class="store-card__media-overlay" />
          <span v-if="merchant.featured_label" class="store-card__badge">{{ merchant.featured_label }}</span>
        </div>

        <div class="store-card__body">
          <div class="store-card__identity">
            <div class="store-card__logo">
              <v-img
                v-if="merchant.logo?.thumb_url"
                class="merchant-logo"
                :aspect-ratio="1"
                :src="merchant.logo.thumb_url"
                width="44"
                height="44"
                cover
              />
              <span v-else>{{ getInitials(merchant) }}</span>
            </div>

            <div class="store-card__text">
              <div class="store-card__name">
                {{ merchant.business_name }}
              </div>
              <div class="store-card__subtitle">
                {{ merchantCategory }}
              </div>
            </div>
          </div>
        </div>
      </NuxtLink>

      <NuxtLink v-if="topStores" to="/marketplace" class="store-card store-card--view-all">
        <div class="store-card__media store-card__media--cta">
          <v-icon class="store-card__icon" size="54">mdi-storefront-outline</v-icon>
        </div>

        <div class="store-card__body">
          <div class="store-card__text">
            <div class="store-card__name">View all stores</div>
            <div class="store-card__subtitle">Browse the full marketplace</div>
          </div>
          <div class="store-card__cta">
            Explore marketplace
            <v-icon size="18">mdi-arrow-right</v-icon>
          </div>
        </div>
      </NuxtLink>
    </div>
  </div>
  </section>
</template>

<style lang="scss" scoped>
.nearby-stores {
  width: 100%;
  padding-bottom: 80px !important;
}

.nearby-stores-title {
  font-size: 2.5rem;
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.5px;
  text-align: left;
  margin-bottom: 32px;  
}



.stores__content {
  display: flex;
  gap: 16px;
}

.stores-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 22px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.store-card,
.store-card-skeleton {
  display: flex;
  flex-direction: column;
  min-width: 300px;
  border-radius: 20px;
  overflow: hidden;
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgba(var(--v-theme-stroke), 0.95);
  text-decoration: none;
  transition:
    transform 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease;

  @media (max-width: 768px) {
    min-width: 280px;
  }
}

.store-card:hover {
  transform: translateY(-1px);
  border-color: rgba(var(--v-theme-primary), 0.7);
}

.store-card__media {
  position: relative;
  aspect-ratio: 16 / 9;
  border-bottom: 1px solid rgba(var(--v-theme-stroke), 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.store-card__media--cta {
  background: linear-gradient(180deg, rgba(var(--v-theme-primary), 0.14) 0%, rgba(var(--v-theme-primary), 0.04) 100%);
}

.store-card__cover,
.store-card__placeholder,
.store-card__media-overlay {
  position: absolute;
  inset: 0;
}

.store-card__cover {
  width: 100%;
  height: 100%;
}

.store-card__placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 20px;
  background:
    radial-gradient(circle at top, rgba(var(--v-theme-primary), 0.12), transparent 58%),
    linear-gradient(180deg, rgba(var(--v-theme-surface-light), 0.55) 0%, rgba(var(--v-theme-background), 0.85) 100%);
}

.store-card__media-overlay {
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.04) 0%, rgba(0, 0, 0, 0.45) 100%);
}

.store-card__badge {
  position: absolute;
  top: 14px;
  left: 14px;
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgb(var(--v-theme-primary));
  color: rgb(var(--v-theme-background));
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
  z-index: 1;
}

.store-card__icon {
  color: rgba(var(--v-theme--primary), 0.14);
}

.store-card__placeholder-text {
  max-width: 220px;
  color: rgba(var(--v-theme--primary), 0.45);
  font-size: 14px;
  font-weight: 700;
  line-height: 1.3;
  text-align: center;
  display: -webkit-box;
  overflow: hidden;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.store-card__body {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 14px;
  padding: 18px 18px 20px;
}

.store-card__identity {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.store-card__logo {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border-radius: 12px;
  overflow: hidden;
  background: rgba(var(--v-theme-background), 0.7);
  border: 1px solid rgba(var(--v-theme-stroke), 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgb(var(--v-theme-primary));
  font-size: 20px;
  font-weight: 800;
}

.merchant-logo {
  width: 100%;
  height: 100%;
}

.store-card__text {
  min-width: 0;
}

.store-card__name {
  color: rgb(var(--v-theme--primary));
  font-size: 17px;
  font-weight: 800;
  line-height: 1.25;
  display: -webkit-box;
  overflow: hidden;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
}

.store-card__subtitle {
  margin-top: 2px;
  color: rgb(var(--v-theme--secondary));
  font-size: 14px;
  line-height: 1.4;
}

.store-card__cta {
  margin-top: auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: rgb(var(--v-theme-primary));
  font-size: 14px;
  font-weight: 700;
}

.mobile-card {
  width: 320px;
}

.store-card--view-all {
  justify-content: space-between;
}
</style>
